import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Typography,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Link as RouterLink } from 'react-router-dom';
import { aiApi } from '../../api/ai_api';

interface FieldGuideSuggestionsCardProps {
  requestKey: string;
  requestUuid: string;
}

interface FieldGuideMatch {
  section_name: string;
  title: string;
  content: string;
  uuid: string;
  slug: string;
  section_slug: string;
}

export function FieldGuideSuggestionsCard({ requestKey, requestUuid }: FieldGuideSuggestionsCardProps) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<FieldGuideMatch[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!requestUuid || fetched) return;

    // Only fetch existing suggestions — don't trigger new AI generation
    // to avoid spamming the Gemini API on every task view
    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const items = await aiApi.getSuggestions(requestUuid, 'fieldguide_suggestion');
        if (items.items.length > 0) {
          const content = items.items[0].content;
          const lines = content.split('\n').filter((l: string) => l.trim() && l.trim().toLowerCase() !== 'none');
          setSuggestions(lines.map((line: string) => ({
            section_name: '',
            title: line.replace(/^[-*]\s*/, '').trim(),
            content: '',
            uuid: '',
            slug: '',
            section_slug: 'general',
          })));
        }
      } catch {
        // Silently fail - suggestions are optional
      } finally {
        setLoading(false);
        setFetched(true);
      }
    };

    fetchSuggestions();
  }, [requestKey, requestUuid, fetched]);

  if (!loading && suggestions.length === 0) return null;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <MenuBookIcon color="secondary" sx={{ fontSize: 20 }} />
          <Typography variant="subtitle2">Suggested Field Guide</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {suggestions.map((s, i) => (
              <Box key={i}>
                <Chip
                  label={s.title}
                  size="small"
                  variant={expanded === s.title ? 'filled' : 'outlined'}
                  color="secondary"
                  onClick={() => setExpanded(expanded === s.title ? null : s.title)}
                  deleteIcon={expanded === s.title ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onDelete={() => setExpanded(expanded === s.title ? null : s.title)}
                  sx={{ maxWidth: '100%' }}
                />
                <Collapse in={expanded === s.title}>
                  <Box sx={{ pl: 1, pt: 0.5, pb: 0.5 }}>
                    {s.content ? (
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {s.content.slice(0, 300)}
                        {s.content.length > 300 && '...'}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        component={RouterLink}
                        to={`/assistant/fieldguide/${s.section_slug}`}
                        sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                      >
                        View in Field Guide
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
